import { useEffect, useRef, useState } from 'react';
import { markUserEdit } from '../../hooks/useUnsavedGuard';
import './DxRichEditor.css';

/**
 * The DevExpress v20.1 Rich Text Editor — the SAME Word-style ribbon editor legacy ALPACA
 * (G5) shows on rich-text documents (File · Home · Insert · Page Layout · References · View),
 * so an article is edited here exactly as it is there (Technical Writer, owner request
 * 2026-10-13: "i need what g5 have, same like here").
 *
 * The value is the document as **RTF** — G5's own storage format — so a round trip loses
 * nothing: no HTML conversion sits between the editor and the database.
 *
 * Isolation: the control runs in a same-origin `srcdoc` frame. Its DevExtreme theme
 * (dx.light.css) styles the whole document it lives in; inside the frame it cannot leak
 * onto the app's own controls. The scripts are the pure-browser npm builds
 * (`devextreme` + `devexpress-richedit`, plus the JSZip they require) from jsDelivr / cdnjs,
 * pinned to **20.1.4** — the version
 * and source the DevExpress report/dashboard host already uses (ADR-0014 / ADR-0016), so
 * no new vendor or package is added to the app bundle.
 */
export interface DxRichEditorProps {
  /** The document as RTF. Empty = a new, blank document. */
  value: string;
  /** Called with the new RTF after each user edit (never on load). */
  onChange: (rtf: string) => void;
  /** Read-only: the document is shown but cannot be changed. */
  readOnly?: boolean;
  /** CSS height of the editor (ribbon included). */
  height?: string;
  /** Name offered for File ▸ Save As (download). */
  documentName?: string;
}

const DX_VERSION = '20.1.4';
const CDN = 'https://cdn.jsdelivr.net/npm';

/** The frame's page: DevExtreme + RichEdit client resources and one host element. */
const FRAME_HTML = `<!DOCTYPE html>
<html><head><meta charset="utf-8" />
<link rel="stylesheet" href="${CDN}/devextreme@${DX_VERSION}/dist/css/dx.common.css" />
<link rel="stylesheet" href="${CDN}/devextreme@${DX_VERSION}/dist/css/dx.light.css" />
<link rel="stylesheet" href="${CDN}/devexpress-richedit@${DX_VERSION}/dist/dx.richedit.css" />
<script src="https://cdnjs.cloudflare.com/ajax/libs/jszip/3.5.0/jszip.min.js"></script>
<script src="${CDN}/devextreme@${DX_VERSION}/dist/js/dx.all.js"></script>
<script src="${CDN}/devexpress-richedit@${DX_VERSION}/dist/dx.richedit.min.js"></script>
<style>html,body{margin:0;padding:0;height:100%;overflow:hidden}#re{position:absolute;inset:0}</style>
</head><body><div id="re"></div></body></html>`;

/* Values of the 20.1 client enums (dx.richedit.d.ts), named here for readability. */
const DOC_FORMAT_RTF = 2; // DocumentFormat.Rtf
const TAB_MAIL_MERGE = 5; // RibbonTabType.MailMerge — not on the G5 ribbon
const TAB_FILE = 0; // RibbonTabType.File
const FILE_NEW = 412; // FileTabItemId.CreateNewDocument — G5's File tab has no New
const FILE_EXPORT = 413; // FileTabItemId.ExportDocument — needs a server exportUrl
const FILE_DOWNLOAD = 414; // FileTabItemId.Download — G5 captions it "Save As"
const VIEW_SIMPLE = 0; // ViewType.Simple — no page edges, the document fills the box (G5)

/** RTF text ⇄ base64 (UTF-8; DevExpress writes RTF as ASCII with \uN escapes). */
const toBase64 = (text: string) => {
  const bytes = new TextEncoder().encode(text);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
};
const fromBase64 = (b64: string) => {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder('utf-8').decode(bytes);
};

/* Minimal shape of the client API this component uses (devexpress-richedit 20.1). */
interface DxRibbonItem { text?: string; localizationId?: string }
interface DxRibbonTab { removeItem(id: number): unknown; getItem(id: number): DxRibbonItem | null }
interface DxRichEdit {
  readOnly: boolean;
  ribbon: { removeTab(id: number): unknown; getTab(id: number): DxRibbonTab | null; activeTabIndex: number };
  openDocument(base64?: string, name?: string, format?: number, cb?: (ok: boolean) => void): void;
  newDocument(): void;
  exportToBase64(cb: (base64: string) => void, format?: number): void;
  dispose(): void;
}
interface DxRichEditApi {
  createOptions(): Record<string, unknown> & { ribbon: DxRichEdit['ribbon']; events: Record<string, unknown> };
  create(el: HTMLElement, options: unknown): DxRichEdit;
}

export function DxRichEditor({ value, onChange, readOnly = false, height = '600px', documentName = 'Article' }: DxRichEditorProps) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const editorRef = useRef<DxRichEdit | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  /** The RTF currently in the editor — set on open and on every export, so a value the
   *  parent hands back that we produced ourselves is not re-opened (that would reset the caret). */
  const currentRef = useRef<string | null>(null);
  /** False while a document is being opened, so the load itself is not reported as an edit. */
  const readyRef = useRef(false);
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');

  const open = (rtf: string) => {
    const re = editorRef.current;
    if (!re) return;
    readyRef.current = false;
    currentRef.current = rtf;
    const done = () => {
      readyRef.current = true;
    };
    if (rtf.trim() === '') {
      re.newDocument();
      done();
    } else {
      re.openDocument(toBase64(rtf), documentName, DOC_FORMAT_RTF, done);
    }
  };

  // Create the control once the frame (and its blocking scripts) has loaded.
  const onFrameLoad = () => {
    const frame = frameRef.current;
    const win = frame?.contentWindow as (Window & { DevExpress?: { RichEdit?: DxRichEditApi } }) | null;
    const api = win?.DevExpress?.RichEdit;
    const host = frame?.contentDocument?.getElementById('re');
    if (!frame || !win || !api || !host) {
      setState('failed');
      return;
    }
    const options = api.createOptions();
    options.width = '100%';
    options.height = '100%';
    options.readOnly = readOnly;
    // The form's own unsaved-changes guard (standing rule 15) owns this prompt.
    options.confirmOnLosingChanges = { enabled: false };
    // G5's ribbon: File · Home · Insert · Page Layout · References · View, opened on File,
    // whose items are Open · Save As · Print; the document in the simple (page-less) view.
    options.ribbon.removeTab(TAB_MAIL_MERGE);
    options.ribbon.activeTabIndex = 0;
    const file = options.ribbon.getTab(TAB_FILE);
    file?.removeItem(FILE_NEW);
    file?.removeItem(FILE_EXPORT);
    const download = file?.getItem(FILE_DOWNLOAD);
    if (download) {
      download.text = 'Save As';
      download.localizationId = undefined; // else the built-in caption wins over `text`
    }
    options.view = { viewType: VIEW_SIMPLE, simpleViewSettings: { paddings: { left: 15, top: 15, right: 15, bottom: 15 } } };
    options.events.documentChanged = () => {
      if (!readyRef.current) return;
      markUserEdit(frameRef.current);
      editorRef.current?.exportToBase64((b64) => {
        const rtf = fromBase64(b64);
        currentRef.current = rtf;
        onChangeRef.current(rtf);
      }, DOC_FORMAT_RTF);
    };
    editorRef.current = api.create(host, options);
    open(value);
    setState('ready');

    // Save shortcuts typed INSIDE the frame reach the form's shared hotkeys (rule 14):
    // the frame is its own document, so its keystrokes never bubble to the app.
    frame.contentDocument?.addEventListener(
      'keydown',
      (e) => {
        const mod = e.ctrlKey || e.metaKey;
        if (!mod || !(e.key.toLowerCase() === 's' || e.key === 'Enter')) return;
        e.preventDefault();
        e.stopPropagation();
        // On the frame element: it bubbles to `document`, where the shared hotkey store listens.
        frameRef.current?.dispatchEvent(
          new KeyboardEvent('keydown', { key: e.key, ctrlKey: e.ctrlKey, metaKey: e.metaKey, shiftKey: e.shiftKey, bubbles: true }),
        );
      },
      true,
    );
  };

  // A different document from outside (Refresh, prev/next record, a new record).
  useEffect(() => {
    if (state === 'ready' && value !== currentRef.current) open(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, state]);

  useEffect(() => {
    if (editorRef.current) editorRef.current.readOnly = readOnly;
  }, [readOnly]);

  useEffect(
    () => () => {
      editorRef.current?.dispose();
      editorRef.current = null;
    },
    [],
  );

  return (
    <div className="dxre-wrap" style={{ height }}>
      <iframe
        ref={frameRef}
        className="dxre-frame"
        title="Rich text editor"
        srcDoc={FRAME_HTML}
        onLoad={onFrameLoad}
      />
      {state === 'loading' && <div className="dxre-msg">Loading editor…</div>}
      {state === 'failed' && (
        <div className="dxre-msg dxre-msg--error" role="alert">
          The editor could not be loaded. It needs access to cdn.jsdelivr.net.
        </div>
      )}
    </div>
  );
}
